'use client';

import { useState, useMemo, useRef, useEffect } from 'react';

interface Certificate {
  id: string;
  year: number;
  cat: string;
  issuer: string;
  status: string;
  code: string;
  title: string;
  image: string;
  file: string;
  verify: string;
  tags: string[];
  note: string;
}

const CERTS: Certificate[] = [
  {
    id: 'c1', year: 2025, cat: 'Безопасность', issuer: 'ISO/IEC', status: 'Действует',
    code: 'ISO/IEC 27001:2022', title: 'Система менеджмента информационной безопасности',
    image: 'https://placehold.co/800x560?text=ISO+27001',
    file: '#', verify: '#', tags: ['ISMS', 'Risk', 'Audit'],
    note: 'Scope: защита персональных данных кандидатов/клиентов.'
  },

  {
    id: 'c2', year: 2025, cat: 'Качество', issuer: 'ISO/IEC', status: 'Действует',
    code: 'ISO 9001:2015', title: 'Система менеджмента качества',
    image: 'https://placehold.co/800x560?text=ISO+9001',
    file: '#', verify: '#', tags: ['QMS', 'SLA', 'KPI'],
    note: 'Процессы подбора/аутсорсинга, контроль качества.'
  },

  {
    id: 'c3', year: 2025, cat: 'Правовое соответствие', issuer: 'APEC/ЕС', status: 'Действует',
    code: 'GDPR/International Transfer', title: 'Процессы и DPA',
    image: 'https://placehold.co/800x560?text=GDPR',
    file: '#', verify: '#', tags: ['DPA', 'Privacy', 'Lawful Basis'],
    note: 'Права субъектов, минимизация данных, ретенция.'
  },

  {
    id: 'c4', year: 2025, cat: 'Безопасность', issuer: 'Audit Firm', status: 'На проверке',
    code: 'SOC 2 Type I', title: 'Security/Availability/Confidentiality',
    image: 'https://placehold.co/800x560?text=SOC+2+Type+I',
    file: '#', verify: '#', tags: ['Trust', 'Controls', 'Report'],
    note: 'Отчёт доступен под NDA.'
  },

  {
    id: 'c5', year: 2025, cat: 'HR/Рекрутинг', issuer: 'HR Association', status: 'Действует',
    code: 'Recruitment Excellence', title: 'Стандарты найма и этики',
    image: 'https://placehold.co/800x560?text=HR+Recruitment',
    file: '#', verify: '#', tags: ['Ethics', 'Equal Opportunity'],
    note: 'Равные возможности, анти-дискриминация.'
  },

  {
    id: 'c6', year: 2025, cat: 'Безопасность', issuer: 'ISO/IEC', status: 'Истёк',
    code: 'ISO/IEC 27017', title: 'Безопасность облачных сервисов',
    image: 'https://placehold.co/800x560?text=ISO+27017',
    file: '#', verify: '#', tags: ['Cloud', 'Controls'],
    note: 'На продление в Q4.'
  },

  {
    id: 'c7', year: 2025, cat: 'Качество', issuer: 'Audit Firm', status: 'Действует',
    code: 'Process Audit', title: 'Внешний аудит процессов',
    image: 'https://placehold.co/800x560?text=Process+Audit',
    file: '#', verify: '#', tags: ['KPI', 'SOP'],
    note: 'Регламенты, SOP, контроль исполнения.'
  },

  {
    id: 'c8', year: 2025, cat: 'Правовое соответствие', issuer: 'IAPP', status: 'Действует',
    code: 'CIPP/E', title: 'Сертифицированные специалисты по приватности',
    image: 'https://placehold.co/800x560?text=CIPP%2FE',
    file: '#', verify: '#', tags: ['Privacy', 'EU'],
    note: 'Команда compliance прошла обучение и экзамены.'
  },

  {
    id: 'c9', year: 2025, cat: 'HR/Рекрутинг', issuer: 'HR Association', status: 'Действует',
    code: 'Fair Hiring', title: 'Честный найм',
    image: 'https://placehold.co/800x560?text=Fair+Hiring',
    file: '#', verify: '#', tags: ['Candidate Experience', 'DEI'],
    note: 'Прозрачные процессы, обратная связь каждому.'
  },
];

function badgeClass(status: string): string {
  if (status === 'Действует') return 'badge ok';
  if (status === 'На проверке') return 'badge warn';
  return 'badge err';
}

const formatYear = (year: number): string => year.toString();

const allYears = CERTS.map(c => c.year);
const minYear = allYears.length > 0 ? Math.min(...allYears) : new Date().getFullYear();
const maxYear = allYears.length > 0 ? Math.max(...allYears) : new Date().getFullYear();

const Kpis = () => {
  const kpiData = [
    { label: 'активных сертификатов', value: 9 },
    { label: 'критических несоответствий', value: 0 },
    { label: 'месяцев средний срок действия', value: 12 },
    { label: 'предстоящих продлений', value: 2 }
  ];
  const counterRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const el = entry.target as HTMLDivElement;
            const to = Number(el.dataset.to);
            const duration = 900;
            const start = performance.now();

            const step = (now: number) => {
              const progress = Math.min(1, (now - start) / duration);
              el.textContent = `${Math.floor(to * progress)}`;
              if (progress < 1) {
                requestAnimationFrame(step);
              }
            };

            requestAnimationFrame(step);
            observer.unobserve(el);
          }
        });
      },
      { threshold: 0.5 }
    );

    counterRefs.current.forEach(ref => {
      if (ref) {
        observer.observe(ref);
      }
    });

    return () => {
      counterRefs.current.forEach(ref => {
        if (ref) {
          observer.unobserve(ref);
        }
      });
    };
  }, []);

  return (
    <section id="kpi" className="grid gap-6 md:grid-cols-4">
      {kpiData.map((kpi, index) => (
        <article key={kpi.label} className="card rounded-2xl p-6 text-center">
          <div
            ref={el => { counterRefs.current[index] = el; }}
            className="text-5xl font-extrabold counter"
            data-to={kpi.value}
          >
            0
          </div>
          <div className="text-xs text-gray-300 mt-1">{kpi.label}</div>
        </article>
      ))}
    </section>
  );
};

interface CertCardProps {
  cert: Certificate;
  onOpenModal: (cert: Certificate) => void;
}

const CertCard = ({ cert, onOpenModal }: CertCardProps) => {
  const { code, year, cat, issuer, status, title, image, tags } = cert;
  return (
    <article className="card rounded-2xl overflow-hidden flex flex-col">
      <img
        src={image}
        alt=""
        style={{ width: '100%', height: '11rem', objectFit: 'cover' }}
        className="w-full h-44 object-cover"
      />
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xl font-semibold">{code}</h3>
          <span className="tag">{formatYear(year)}</span>
        </div>
        <div className="mt-2 text-xs flex flex-wrap gap-2">
          <span className="tag">{cat}</span>
          <span className="tag">{issuer}</span>
          <span className={`${badgeClass(status)}`}>{status}</span>
        </div>
        <p className="text-sm text-gray-200 mt-3">{title}</p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {tags.map(t => <span key={t} className="tag">{t}</span>)}
        </div>
        <div className="mt-4 flex flex-col gap-2">
          <div className="text-xs text-gray-300 mono">ID: {cert.id}</div>
          <div className="flex gap-2 justify-end">
            <a href={cert.file} className="btn btn-ghost text-sm">Скачать PDF</a>
            <button className="btn btn-accent text-sm" onClick={() => onOpenModal(cert)}>Подробнее</button>
          </div>
        </div>
      </div>
    </article>
  );
};


interface CertModalProps {
  cert: Certificate;
  onClose: () => void;
}

const CertModal = ({ cert, onClose }: CertModalProps) => {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="rounded-2xl p-6 max-w-3xl w-[92%] relative my-8 bg-blue-900 text-white"
        onClick={e => e.stopPropagation()}
      >
        <button
          className="absolute right-3 top-3 px-2 py-1 rounded-lg btn-ghost"
          onClick={onClose}
        >
          ✕
        </button>
        <div className="grid gap-4 md:grid-cols-2">
          <img
            src={cert.image}
            alt={`Изображение сертификата ${cert.code}`}
            style={{ width: '100%', height: '14rem', objectFit: 'cover' }}
            className="rounded-xl w-full h-56 object-cover"
          />
          <div>
            <h3 className="text-2xl font-bold">{cert.code}</h3>
            <div className="mt-2 text-xs flex flex-wrap gap-2">
              <span className="tag">{cert.cat}</span>
              <span className="tag">{cert.issuer}</span>
              <span className={`${badgeClass(cert.status)}`}>{cert.status}</span>
              <span className="tag">{formatYear(cert.year)}</span>
            </div>
            <p className="text-sm text-gray-200 mt-3">{cert.title}</p>
            <p className="text-sm text-gray-300 mt-1">{cert.note || ''}</p>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {cert.tags.map(t => <span key={t} className="tag">{t}</span>)}
            </div>
            <div className="mt-4 flex gap-2">
              <a href={cert.file} className="btn btn-ghost">Скачать PDF</a>
              <a href={cert.verify} className="btn btn-accent">Проверить</a>
            </div>
            <div className="mt-3 text-xs text-gray-300 mono">ID: {cert.id}</div>
          </div>
        </div>
      </div>
    </div>
  );
};


const categories = [...new Set(CERTS.map(c => c.cat))];
const issuers = [...new Set(CERTS.map(c => c.issuer))];
const statuses = [...new Set(CERTS.map(c => c.status))];

export default function CertsPage() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [issuer, setIssuer] = useState('');
  const [status, setStatus] = useState('');
  const [yearRange, setYearRange] = useState({ start: minYear, end: maxYear });
  const [sort, setSort] = useState('year');
  const [selectedCert, setSelectedCert] = useState<Certificate | null>(null);

  const filteredCerts = useMemo(() => {
    let items = CERTS.filter(c => {
      const q = query.toLowerCase();
      const matchesQuery = !q || (c.code + ' ' + c.title + ' ' + c.issuer).toLowerCase().includes(q);
      const matchesCat = !category || c.cat === category;
      const matchesIssuer = !issuer || c.issuer === issuer;
      const matchesStatus = !status || c.status === status;
      const matchesYear = c.year >= yearRange.start && c.year <= yearRange.end;

      return matchesQuery && matchesCat && matchesIssuer && matchesStatus && matchesYear;
    });

    switch (sort) {
      case 'year':
        items.sort((a, b) => b.year - a.year);
        break;
      case 'status':
        items.sort((a, b) => a.status.localeCompare(b.status));
        break;
      case 'name':
        items.sort((a, b) => a.code.localeCompare(b.code));
        break;
      default:
        break;
    }
    return items;
  }, [query, category, issuer, status, yearRange, sort]);

  const handleStartYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStart = parseInt(e.target.value);
    if (newStart <= yearRange.end) {
      setYearRange(prev => ({ ...prev, start: newStart }));
    }
  };

  const handleEndYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEnd = parseInt(e.target.value);
    if (newEnd >= yearRange.start) {
      setYearRange(prev => ({ ...prev, end: newEnd }));
    }
  };

  return (
    <>
      <div className="bg-gradient-to-br from-blue-900 to-blue-800 text-gray-100 min-h-screen">
        <header
          className="px-6 pt-32 pb-16 md:pt-40 md:pb-20"
          style={{ background: 'linear-gradient(rgba(0,0,0,.45), rgba(0,0,0,.25)), radial-gradient(1200px 600px at 70% -10%, rgba(255,255,255,.08), transparent)' }}
        >
          <div className="max-w-7xl mx-auto">
            <nav className="mb-6 flex flex-wrap gap-3 text-sm">
              <a href="#portfolio" className="chip px-3 py-1 rounded-full hover:bg-white/25">Все сертификаты</a>
              <a href="#verify" className="chip px-3 py-1 rounded-full hover:bg-white/25">Проверка подлинности</a>
              <a href="#faq" className="chip px-3 py-1 rounded-full hover:bg-white/25">FAQ</a>
              <a href="#cta" className="chip px-3 py-1 rounded-full hover:bg-white/25">Запросить копии</a>
            </nav>

            <h1 className="text-4xl md:text-6xl font-extrabold mb-4">Сертификаты и соответствие</h1>
            <p className="max-w-3xl text-lg md:text-xl text-gray-200">
              Безопасность данных, качество процессов и соответствие требованиям. Здесь вы найдёте актуальные сертификаты и инструкции по верификации.
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#portfolio" className="btn btn-ghost">Смотреть список</a>
              <a href="#verify" className="btn btn-accent">Как проверить</a>
            </div>
          </div>
        </header>

        <main className="px-6 py-12 md:py-16 max-w-7xl mx-auto space-y-16">
          <Kpis />

          <section id="portfolio" className="scroll-mt-32">
            <div className="flex flex-wrap items-end gap-4 justify-between">
              <h2 className="text-3xl md:text-4xl font-bold">Список сертификатов</h2>
              <span id="count" className="text-sm text-gray-300">Найдено: {filteredCerts.length}</span>
            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-6">
              <input
                id="q"
                className="rounded-xl px-4 py-3 text-gray-900 md:col-span-2"
                placeholder="Поиск по названию/органу…"
                value={query}
                onChange={e => setQuery(e.target.value)}
              />

              <select
                id="sort"
                className="rounded-xl px-4 py-3 text-gray-900"
                value={sort}
                onChange={e => setSort(e.target.value)}
              >
                <option value="year">Сортировать: по году ↓</option>
                <option value="status">по статусу</option>
                <option value="name">по названию</option>
              </select>

              <select
                id="cat"
                className="rounded-xl px-4 py-3 text-gray-900"
                value={category}
                onChange={e => setCategory(e.target.value)}
              >
                <option value="">Категория: все</option>
                {categories.map(cat => <option key={cat}>{cat}</option>)}
              </select>

              <select
                id="issuer"
                className="rounded-xl px-4 py-3 text-gray-900"
                value={issuer}
                onChange={e => setIssuer(e.target.value)}
              >
                <option value="">Орган: все</option>
                {issuers.map(iss => <option key={iss}>{iss}</option>)}
              </select>

              <select
                id="status"
                className="rounded-xl px-4 py-3 text-gray-900"
                value={status}
                onChange={e => setStatus(e.target.value)}
              >
                <option value="">Статус: все</option>
                {statuses.map(st => <option key={st}>{st}</option>)}
              </select>

              <div className="rounded-xl px-4 py-3 card flex flex-col justify-center md:col-span-2">
                <div className="flex justify-between items-center text-sm text-gray-200 mb-1">
                  <label>Годы:</label>
                  <span>{formatYear(yearRange.start)} – {formatYear(yearRange.end)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    id="y1"
                    type="range"
                    min={minYear}
                    max={maxYear}
                    value={yearRange.start}
                    onChange={handleStartYearChange}
                    className="flex-1 range range-primary"
                  />
                  <input
                    id="y2"
                    type="range"
                    min={minYear}
                    max={maxYear}
                    value={yearRange.end}
                    onChange={handleEndYearChange}
                    className="flex-1 range range-primary"
                  />
                </div>
              </div>
            </div>

            <div id="grid" className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filteredCerts.length > 0 ? (
                filteredCerts.map(cert => (
                  <CertCard key={cert.id} cert={cert} onOpenModal={setSelectedCert} />
                ))
              ) : (
                <p id="empty" className="text-gray-300 md:col-span-3">Ничего не найдено — измените фильтры.</p>
              )}
            </div>
          </section>

          <section id="verify" className="scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Проверка подлинности</h2>
            <div className="grid gap-6 md:grid-cols-3">
              <article className="card rounded-2xl p-6">
                <h3 className="text-xl font-semibold">1) Проверка реквизитов</h3>
                <p className="text-sm text-gray-200 mt-1">Сверьте номер, дату, орган, и срок действия. В карточке «Подробнее» есть ID и ссылка на верификацию.</p>
              </article>
              <article className="card rounded-2xl p-6">
                <h3 className="text-xl font-semibold">2) Онлайновая верификация</h3>
                <p className="text-sm text-gray-200 mt-1">Перейдите по ссылке «Проверить» — откроется страница реестра/органа с нашим сертификатом.</p>
              </article>
              <article className="card rounded-2xl p-6">
                <h3 className="text-xl font-semibold">3) Запрос оригинала</h3>
                <p className="text-sm text-gray-200 mt-1">Нужна заверенная копия? Оставьте запрос ниже — вышлем PDF/скан и контакт ответственного.</p>
              </article>
            </div>
          </section>

          <section id="faq" className="scroll-mt-32">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">FAQ</h2>
            <div className="space-y-3">
              <details className="simple-card">
                <summary className="cursor-pointer font-semibold">Где хранятся оригиналы?</summary>
                <p className="mt-2 text-sm text-gray-200">В безопасном хранилище с контролем доступа; публичные копии — в формате PDF.</p>
              </details>
              <details className="simple-card">
                <summary className="cursor-pointer font-semibold">Как часто проходим аудит?</summary>
                <p className="mt-2 text-sm text-gray-200">Плановые ежегодные проверки и внутренние квартальные ревью.</p>
              </details>
              <details className="simple-card">
                <summary className="cursor-pointer font-semibold">Можно использовать наш шаблон DPA/NDA?</summary>
                <p className="mt-2 text-sm text-gray-200">Да, согласуем правки с юристами и приложим к договору.</p>
              </details>
            </div>
          </section>

          <section
            id="cta"
            className="simple-card flex flex-col md:flex-row md:items-center md:justify-between scroll-mt-32"
          >
            <div>
              <h3 className="text-2xl font-bold">Нужны заверенные копии?</h3>
              <p className="text-sm text-gray-200 mt-1">Оставьте контакт — вышлем подборку нужных сертификатов и сопроводительные письма.</p>
            </div>
            <a href="/contacts" className="btn btn-accent mt-4 md:mt-0">Запросить копии</a>
          </section>
        </main>

        {selectedCert && <CertModal cert={selectedCert} onClose={() => setSelectedCert(null)} />}
      </div>
    </>
  );
}
